<template>
  <div>
    <div class="barra-ferramentas">
      <div class="campo-busca">
        <fa-icon icon="search" class="campo-busca__icone" />
        <input
          v-model="busca"
          type="search"
          class="form-control form-control-sm"
          placeholder="Buscar por nome, conector ou provedor..."
        />
      </div>

      <StatusFilterBar v-model="statusAtivo" :chips="chips" :contagens="contagens" />
    </div>

    <div v-if="itens.length" class="grade-integracoes">
      <template v-for="item in itens">
        <CardGrupoIntegracao
          v-if="item.grupo"
          :key="item.chave"
          :integracoes="item.integracoes"
          @configurar="$emit('configurar', $event)"
          @replicar="$emit('configurar', $event, { replicar: true })"
        />

        <CardIntegracao
          v-else
          :key="item.chave"
          :integracao="item.integracao"
          :testando="idTestando === item.integracao.id"
          @configurar="$emit('configurar', item.integracao)"
          @testar="testar"
          @alternar-ativacao="alternarAtivacao"
          @remover="remover"
        />
      </template>
    </div>

    <EmptyState
      v-else-if="integracoes.length"
      icon="filter"
      title="Nenhuma integração com esses filtros"
      description="Ajuste a busca ou o filtro de status para ver as demais configurações."
    />

    <EmptyState
      v-else
      icon="plug"
      title="Nenhuma integração configurada"
      description="Conecte a plataforma a serviços externos — telefonia, e-mail, frete, inteligência artificial e mais."
    >
      <template #action>
        <CButton color="primary" @click="$emit('ir-para-marketplace')">
          Ver o catálogo
        </CButton>
      </template>
    </EmptyState>
  </div>
</template>

<script>
import { put, del, post } from '@/helpers/apiRequest';
import EmptyState from '@/components/EmptyState';
import StatusFilterBar from '@/components/StatusFilterBar';
import CardGrupoIntegracao from './components/CardGrupoIntegracao';
import CardIntegracao from './components/CardIntegracao';

export default {
  name: 'IntegracoesConfiguradas',
  components: { CardGrupoIntegracao, CardIntegracao, EmptyState, StatusFilterBar },
  props: {
    integracoes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      busca: '',
      statusAtivo: 'todas',
      idTestando: null,
      chips: [
        { chave: 'todas', label: 'Todas' },
        { chave: 'ativas', label: 'Ativas', dot: true, cor: '#1c7a44' },
        { chave: 'inativas', label: 'Inativas', dot: true, cor: '#667085' },
        { chave: 'falha', label: 'Com falha', dot: true, cor: '#b42318' },
      ],
    };
  },
  computed: {
    /*
     * Busca e filtro rodam no cliente porque a lista de integrações é curta por
     * natureza — uma dezena, não milhares. Ida ao servidor a cada tecla só
     * deixaria a tela mais lenta.
     */
    integracoesFiltradas() {
      const termo = this.busca.trim().toLowerCase();

      return this.integracoes.filter((integracao) => {
        if (!this.atendeStatus(integracao)) {
          return false;
        }

        if (!termo) {
          return true;
        }

        const conector = integracao.conector || {};
        const alvo = [
          integracao.nome,
          integracao.provedor_nome,
          integracao.escopo_nome,
          conector.nome,
          conector.categoria_nome,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return alvo.includes(termo);
      });
    },

    /*
     * O que a grade mostra: um item por configuração, exceto nos conectores
     * cujas instâncias são finalidades de uma mesma coisa — ali as
     * configurações se reúnem num item só.
     *
     * O agrupamento vem depois do filtro, e não antes: filtrando por "com
     * falha", o cartão do grupo precisa listar só as finalidades que falharam,
     * senão o filtro diria uma coisa e o cartão mostraria outra.
     */
    itens() {
      const itens = [];
      const grupos = {};

      this.integracoesFiltradas.forEach((integracao) => {
        const conector = integracao.conector || {};

        if (!conector.agrupa_por_escopo) {
          itens.push({ chave: 'i' + integracao.id, integracao });
          return;
        }

        if (grupos[conector.slug]) {
          grupos[conector.slug].integracoes.push(integracao);
          return;
        }

        grupos[conector.slug] = { chave: 'g' + conector.slug, grupo: true, integracoes: [integracao] };
        itens.push(grupos[conector.slug]);
      });

      return itens;
    },

    contagens() {
      return {
        todas: this.integracoes.length,
        ativas: this.integracoes.filter((i) => i.ativo).length,
        inativas: this.integracoes.filter((i) => !i.ativo).length,
        falha: this.integracoes.filter((i) => i.ativo && i.status_conexao === 'FALHA').length,
      };
    },
  },
  methods: {
    atendeStatus(integracao) {
      if (this.statusAtivo === 'ativas') return integracao.ativo;
      if (this.statusAtivo === 'inativas') return !integracao.ativo;
      // Integração desligada não conta como "com falha": ela não está tentando
      // conectar, então o erro antigo não é um problema em aberto.
      if (this.statusAtivo === 'falha') return integracao.ativo && integracao.status_conexao === 'FALHA';

      return true;
    },

    testar(integracao) {
      this.idTestando = integracao.id;

      post(`/admin/integracoes/${integracao.id}/testar-conexao`, {})
        .then((resposta) => {
          this.$swal({
            title: 'Conexão estabelecida',
            text: resposta.message,
            icon: 'success',
            showConfirmButton: true,
          });
        })
        .catch((erro) => {
          this.$swal({
            title: 'Falha na conexão',
            text: erro.message,
            icon: 'error',
            showConfirmButton: true,
          });
        })
        .finally(() => {
          this.idTestando = null;
          this.$emit('recarregar');
        });
    },

    alternarAtivacao(integracao) {
      put(`/admin/integracoes/${integracao.id}/ativacao`, { ativo: !integracao.ativo })
        .then((resposta) => {
          this.$emit('recarregar');
          this.$swal({
            title: resposta.message,
            icon: 'success',
            timer: 1800,
            showConfirmButton: false,
          });
        })
        .catch((erro) => {
          this.$swal({ title: 'Não foi possível alterar', text: erro.message, icon: 'error' });
        });
    },

    /*
     * Remover é irreversível e derruba a configuração de um serviço em uso, por
     * isso a confirmação explica a alternativa menos drástica: desativar.
     */
    remover(integracao) {
      this.$swal({
        title: 'Remover esta integração?',
        text:
          `"${integracao.nome}" será excluída e suas credenciais serão apagadas. ` +
          'Para apenas interromper o uso sem perder a configuração, desative-a.',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Remover',
      }).then((resultado) => {
        if (!resultado.value) {
          return;
        }

        del(`/admin/integracoes/${integracao.id}`)
          .then((resposta) => {
            this.$emit('recarregar');
            this.$swal({
              title: resposta.message,
              icon: 'success',
              timer: 1800,
              showConfirmButton: false,
            });
          })
          .catch((erro) => {
            this.$swal({ title: 'Não foi possível remover', text: erro.message, icon: 'error' });
          });
      });
    },
  },
};
</script>

<style scoped>
.barra-ferramentas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.campo-busca {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 380px;
}

.campo-busca__icone {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  font-size: 13px;
  pointer-events: none;
}

.campo-busca input {
  padding-left: 32px;
}

.grade-integracoes {
  display: grid;
  /* Mesma medida do catálogo: as duas abas mostram a mesma prateleira. */
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}
</style>
